import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { listUsers } from '@/api/users'
import { UserTable } from '@/components/users/UserTable'
import { UserDrawer } from '@/components/users/UserDrawer'
import { Button } from '@/components/ui/Button'
import { PillToggle } from '@/components/ui/PillToggle'
import { SearchInput } from '@/components/ui/SearchInput'
import { PageHeader } from '@/components/layout/PageHeader'
import { useDebouncedValue } from '@/hooks/useDebouncedValue'

export function UsersPage() {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const [drawer, setDrawer] = useState(false)
  const [inactive, setInactive] = useState(false)
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebouncedValue(search, 250)

  const { data, isFetching, isPlaceholderData } = useQuery({
    queryKey: ['users', inactive, debouncedSearch],
    queryFn: () =>
      listUsers({
        page: 1,
        pageSize: 100,
        includeInactive: inactive,
        search: debouncedSearch,
        role: 'all',
      }),
    placeholderData: keepPreviousData,
  })

  const users = data?.items ?? []
  const tableLoading = isFetching && (isPlaceholderData || !data)

  return (
    <div>
      <PageHeader
        title={t('users.title')}
        actions={
          <Button variant="primary" onClick={() => setDrawer(true)}>
            {t('users.create')}
          </Button>
        }
        filters={
          <>
            <SearchInput
              placeholder={t('users.searchPlaceholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <PillToggle
              checked={inactive}
              onChange={setInactive}
              label={t('users.inactive')}
            />
          </>
        }
      />
      <UserTable users={users} loading={tableLoading} />
      <UserDrawer
        open={drawer}
        onClose={() => setDrawer(false)}
        onSaved={() => {
          void qc.invalidateQueries({ queryKey: ['users'] })
        }}
      />
    </div>
  )
}
